import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { api, ApiError } from '@/client/lib/api';
import { Markdown } from '@/client/components/cms/Markdown';
import { Seo } from '@/client/components/Seo';
import { Button } from '@/client/components/ui/button';
import { isLocale, type Locale } from '@/client/i18n';

type CmsPagePayload = {
  slug: string;
  locale: Locale;
  title: string;
  body: string;
  metaTitle: string | null;
  metaDescription: string | null;
  updatedAt: number;
};

export function CmsPage({ slug }: { slug: string }) {
  const params = useParams();
  const locale: Locale = isLocale(params.locale) ? params.locale : 'sk';
  const { t } = useTranslation();

  const query = useQuery({
    queryKey: ['cms', slug, locale],
    queryFn: () => api<CmsPagePayload>(`/api/v1/cms/${slug}?locale=${locale}`),
    retry: false,
  });

  if (query.isLoading) {
    return (
      <div className="px-6 py-32 text-center font-sans text-body text-text-secondary">
        <div className="inline-flex items-center gap-3">
          <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />
          {t('common.loading')}
        </div>
      </div>
    );
  }

  if (query.isError) {
    if (query.error instanceof ApiError && query.error.status === 404) {
      return (
        <div className="relative">
          <Seo
            title={t('errors.not_found_title')}
            description={t('seo.default_description')}
            locale={locale}
            index={false}
          />
          <section className="mx-auto max-w-xl px-5 sm:px-6 lg:px-8 py-16 sm:py-24">
            <div className="dr-card-strong p-10 sm:p-14 text-center animate-fade-up">
              <p className="dr-eyebrow">404</p>
              <h1 className="mt-3 font-display text-h2 text-primary-dark">
                {t('errors.not_found_title')}
              </h1>
              <Link to={`/${locale}`} className="inline-block mt-8">
                <Button>
                  <ArrowLeft size={16} />
                  {t('errors.back_home')}
                </Button>
              </Link>
            </div>
          </section>
        </div>
      );
    }
    return (
      <div className="px-6 py-32 text-center font-sans text-body text-error">
        {t('errors.generic')}
      </div>
    );
  }

  if (!query.data) return null;

  const updatedDate = new Date(query.data.updatedAt * 1000).toLocaleDateString(
    locale === 'cs' ? 'cs-CZ' : 'sk-SK',
    { day: 'numeric', month: 'long', year: 'numeric' },
  );

  return (
    <article className="relative">
      <Seo
        title={query.data.metaTitle ?? query.data.title}
        description={query.data.metaDescription ?? t('seo.default_description')}
        locale={locale}
      />

      <div
        className="absolute top-10 -left-32 w-[26rem] h-[26rem] rounded-full pointer-events-none animate-float-orb"
        style={{
          background: 'radial-gradient(circle, rgba(193,167,226,0.35) 0%, transparent 65%)',
          filter: 'blur(40px)',
        }}
        aria-hidden
      />
      <div
        className="absolute top-72 -right-32 w-[28rem] h-[28rem] rounded-full pointer-events-none animate-float-orb"
        style={{
          background: 'radial-gradient(circle, rgba(117,59,189,0.16) 0%, transparent 65%)',
          filter: 'blur(48px)',
          animationDelay: '-12s',
        }}
        aria-hidden
      />

      <section className="relative mx-auto max-w-3xl px-5 sm:px-6 lg:px-8 pt-12 sm:pt-16 pb-6">
        <p className="dr-eyebrow animate-fade-up">
          {t('cms.eyebrow', { defaultValue: 'Akadémia' })}
        </p>
        <h1
          className="mt-3 font-display text-[clamp(2rem,5vw,3.5rem)] leading-[1.05] tracking-tight text-primary-dark animate-fade-up"
          style={{ animationDelay: '70ms' }}
        >
          {query.data.title}
        </h1>
        <p
          className="mt-3 font-sans text-caption uppercase tracking-[0.14em] text-text-muted animate-fade-up"
          style={{ animationDelay: '140ms' }}
        >
          {t('cms.last_updated', { defaultValue: 'Aktualizované' })} · {updatedDate}
        </p>
      </section>

      <section className="relative mx-auto max-w-3xl px-5 sm:px-6 lg:px-8 pb-20">
        <div
          className="dr-card-static p-7 sm:p-10 lg:p-12 animate-fade-up"
          style={{ animationDelay: '210ms' }}
        >
          <Markdown source={query.data.body} />
        </div>
      </section>
    </article>
  );
}
